import {
	IconAlarmClockOutline18 as AlarmClock,
	IconEnvelopeCheckOutline18 as MailCheckIcon,
} from "nucleo-ui-outline-18"

import { Button, IssueCompletedIcon, Shortcut } from "@productlane/ui"

import { toast } from "@/components/toast"
import { useUndoContext } from "@/context/undo-context"
import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"

export function ToastTestPage() {
	const { setPendingMessage } = useUndoContext()

	const undoSnooze = () => {
		console.log("Undo snooze")
	}

	if (!isDevEnvironment) {
		return <NotFound />
	}

	return (
		<div className="flex min-h-dvh flex-col items-center justify-center gap-8 bg-gray-50 p-8 dark:bg-gray-900">
			<div className="w-full max-w-4xl space-y-6">
				<h1 className="text-2xl font-bold">Toast Design Test Page</h1>
				<p className="text-sm text-gray-600 dark:text-gray-400">
					Click the buttons below to see different toast variations
				</p>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
					{/* Success Toasts */}
					<ToastSection title="Success Toasts">
						<Button
							onClick={() => {
								toast.success({ title: "Updated" })
							}}
							variant="outline"
						>
							Title only
						</Button>
						<Button
							onClick={() => {
								toast.success({
									title: "Thread snoozed",
									description: "will be snoozed for 1 hour.",
								})
							}}
							variant="outline"
						>
							Title + Description
						</Button>
						<Button
							onClick={() => {
								toast.success({
									title: "Live Chat ended",
									descriptionAction: {
										label: "Thread: Customer Support Request",
										callback: () => {
											console.log("Navigate to thread")
										},
									},
								})
							}}
							variant="outline"
						>
							Title + DescriptionAction
						</Button>
						<Button
							onClick={() => {
								toast.success({
									icon: AlarmClock,
									title: "Thread snoozed",
									description: "will be snoozed for 1 hour.",
									descriptionAction: {
										label: "Thread: Customer Support Request",
										callback: () => {
											console.log("Navigate to thread")
										},
									},
								})
							}}
							variant="outline"
						>
							Title + Description + DescriptionAction + Custom Icon
						</Button>
						<Button
							onClick={() => {
								toast.success({
									icon: IssueCompletedIcon,
									title: "Marked done",
								})
							}}
							variant="outline"
						>
							Title + Custom Icon
						</Button>
					</ToastSection>

					{/* Error Toasts */}
					<ToastSection title="Error Toasts">
						<Button
							onClick={() => {
								toast.error({ title: "Failed to save widget settings" })
							}}
							variant="outline"
						>
							Title only
						</Button>
						<Button
							onClick={() => {
								toast.error({
									title: "Something went wrong",
									description: "this setting through widget settings.",
								})
							}}
							variant="outline"
						>
							Title + Description
						</Button>
						<Button
							onClick={() => {
								toast.error({
									title: "Something went wrong",
									description: "this setting through widget settings.",
									descriptionAction: {
										label: "Enable",
										callback: () => {
											console.log("Navigate to settings")
										},
									},
								})
							}}
							variant="outline"
						>
							Title + Description + DescriptionAction
						</Button>
						<Button
							onClick={() => {
								toast.error({
									title: "Maximum office hours reached",
									description: `You can only add up to 10 office hours entries.`,
								})
							}}
							variant="outline"
						>
							Title + Long Description
						</Button>
					</ToastSection>

					{/* Info Toasts */}
					<ToastSection title="Info Toasts">
						<Button
							onClick={() => {
								toast.info({ title: "Action not allowed" })
							}}
							variant="outline"
						>
							Title only
						</Button>
						<Button
							onClick={() => {
								toast.info({
									title: "Action not allowed",
									description:
										"Only admins have permission to disconnect integrations.",
								})
							}}
							variant="outline"
						>
							Title + Description
						</Button>
						<Button
							onClick={() => {
								toast.info({
									title: "Action not allowed",
									description:
										"Only admins have permission to disconnect integrations.",
									action: {
										label: "View your role",
										callback: () => {
											console.log("View role")
										},
									},
								})
							}}
							variant="outline"
						>
							Title + Description + Action
						</Button>
						<Button
							onClick={() => {
								toast.info({
									title: "Intercom is now connected",
									description: "You can now sync your Intercom data.",
								})
							}}
							variant="outline"
						>
							Title + Description (Success-like)
						</Button>
					</ToastSection>

					{/* Action Toasts */}
					<ToastSection title="Toasts with Actions">
						<Button
							onClick={() => {
								toast.success({
									title: "Message sent",
									action: {
										label: "Undo",
										callback: () => {
											console.log("Undo action")
										},
									},
								})
							}}
							variant="outline"
						>
							Title + Action
						</Button>
						<Button
							onClick={() => {
								toast.success({
									title: "Message sent",
									action: {
										label: "Undo",
										shortcut: <Shortcut>Z</Shortcut>,
										callback: () => {
											console.log("Undo action")
										},
									},
								})
							}}
							variant="outline"
						>
							Title + Action + Shortcut
						</Button>
						<Button
							onClick={() => {
								toast.success({
									title: "Message sent",
									description: "Your message has been delivered successfully.",
									action: {
										label: "Undo",
										shortcut: <Shortcut>Z</Shortcut>,
										callback: () => {
											console.log("Undo action")
										},
									},
								})
							}}
							variant="outline"
						>
							Title + Description + Action + Shortcut
						</Button>
						<Button
							variant="outline"
							onClick={() => {
								toast.success({
									id: "12345",
									icon: AlarmClock,
									title: "Snoozed until Monday",
									time: 8000,
									descriptionAction: {
										label: "Test Thread about something Really important",
										callback: () => console.log("Clicked"),
									},
									onUndo: undoSnooze,
								})
							}}
						>
							Snooze
						</Button>
					</ToastSection>

					{/* Undo Toasts */}
					<ToastSection title="Undo Toasts">
						<Button
							onClick={() => {
								const messageId = `test-message-${Date.now()}`
								setPendingMessage(messageId)
								toast.undo({
									title: "Message sent",
									icon: MailCheckIcon,
									data: {
										messageId,
									},
									layout: "inline",

									action: {
										label: "Undo",
										shortcut: <Shortcut>Z</Shortcut>,
										callback: () => {
											console.log("Undo message", messageId)
										},
									},
								})
							}}
							variant="outline"
						>
							Undo Toast with Action
						</Button>
					</ToastSection>

					{/* Loading Toasts */}
					<ToastSection title="Loading Toasts">
						<Button
							onClick={() => {
								toast.loading({ title: "Loading..." })
							}}
							variant="outline"
						>
							Loading Toast
						</Button>
						<Button
							onClick={() => {
								toast.loading({
									title: "Uploading image",
									description: "Please wait while we process your image.",
								})
							}}
							variant="outline"
						>
							Loading + Description
						</Button>
					</ToastSection>

					{/* Promise Toasts */}
					<ToastSection title="Promise Toasts">
						<Button
							onClick={() => {
								const promise = new Promise((resolve) => {
									setTimeout(() => resolve("Success"), 2000)
								})
								void toast.promise(promise, {
									loading: "Saving changes...",
									success: "Changes saved successfully",
									error: "Failed to save changes",
								})
							}}
							variant="outline"
						>
							Promise Success
						</Button>
						<Button
							onClick={() => {
								const promise = new Promise((_, reject) => {
									setTimeout(() => reject(new Error("Network error")), 2000)
								})
								void toast.promise(promise, {
									loading: "Saving changes...",
									success: "Changes saved successfully",
									error: "Failed to save changes",
								})
							}}
							variant="outline"
						>
							Promise Error
						</Button>
					</ToastSection>

					{/* Complex Combinations */}
					<ToastSection title="Complex Combinations">
						<Button
							onClick={() => {
								toast.success({
									icon: IssueCompletedIcon,
									title: "Moved to Inbox",
									descriptionAction: {
										label: "Thread: Customer Support Request",
										callback: () => {
											console.log("Navigate to thread")
										},
									},
								})
							}}
							variant="outline"
						>
							Success + Icon + DescriptionAction
						</Button>
						<Button
							onClick={() => {
								toast.error({
									title: "The image dimensions are incorrect",
									description: "Please make sure the image is 1200x630 pixels",
								})
							}}
							variant="outline"
						>
							Error + Long Description
						</Button>
						<Button
							onClick={() => {
								toast.info({
									title: "Success",
									description: "Hubspot is now connected",
								})
							}}
							variant="outline"
						>
							Info Success Message
						</Button>
					</ToastSection>

					{/* Edge Cases */}
					<ToastSection title="Edge Cases">
						<Button
							onClick={() => {
								toast.success({
									title: "Marked 5 threads as done.",
								})
							}}
							variant="outline"
						>
							Long Title
						</Button>
						<Button
							onClick={() => {
								toast.success({
									title: "Updated",
									description:
										"This is a very long description that should wrap properly and display correctly in the toast component. It should handle multiple lines gracefully.",
								})
							}}
							variant="outline"
						>
							Very Long Description
						</Button>
						<Button
							onClick={() => {
								toast.success({
									title: "Thread snoozed",
									description: "will be snoozed for 1 hour.",
									descriptionAction: {
										label: "Very Long Thread Title That Should Wrap Properly",
										callback: () => {
											console.log("Navigate")
										},
									},
								})
							}}
							variant="outline"
						>
							Long DescriptionAction
						</Button>
						<Button
							onClick={() => {
								toast.info({
									title: "Update sent",
									description: "Notifications will be sent in 10 seconds",
									action: {
										label: "Undo",
										callback: () => {
											console.log("Undo")
										},
									},
									time: 10000,
									showProgress: true,
								})
							}}
							variant="outline"
						>
							Toast with progress
						</Button>
					</ToastSection>
				</div>
			</div>
		</div>
	)
}

interface ToastSectionProps {
	title: string
	children: React.ReactNode
}

function ToastSection({ title, children }: ToastSectionProps) {
	return (
		<div className="space-y-2 rounded-lg border border-gray-200 p-4 dark:border-gray-800">
			<h2 className="text-sm font-semibold">{title}</h2>
			<div className="flex flex-col gap-2">{children}</div>
		</div>
	)
}
